/**
 * ECS Type Definitions
 * Mirrors Qud's entity-component architecture
 */

import type { GameObject } from './GameObject';

/**
 * Part interface - components attached to GameObjects
 */
export interface IPart {
  readonly name: string;
  parent: GameObject | null;

  /** Called when part is attached to a GameObject */
  attach(obj: GameObject): void;

  /** Called when part is detached from a GameObject */
  detach(): void;

  /**
   * Handle an event fired on the parent GameObject
   * @returns false to stop event propagation, true to continue
   */
  onEvent(event: string, data?: unknown): boolean;
}

/**
 * Factory function for creating parts with parameters
 */
export type PartFactory = (params: Record<string, unknown>) => IPart;

/**
 * Simple blueprint data format
 * Preserves inheritance hierarchy - resolution happens at runtime
 */
export interface BlueprintData {
  /** All blueprints keyed by name */
  blueprints: Record<string, RawBlueprintDef>;
}

/**
 * Raw blueprint definition as stored in JSON
 * Inheritance is NOT resolved - stores only what's defined on this blueprint
 */
export interface RawBlueprintDef {
  /** Parent blueprint name (if any) */
  inherits?: string;
  /** Parts defined on this blueprint (not inherited) */
  parts: PartDef[];
  /** Builders defined on this blueprint (not inherited) */
  builders: PartDef[];
  /** Tags defined on this blueprint (not inherited) */
  tags: Record<string, string>;
  /** Stats defined on this blueprint (not inherited) */
  stats: Record<string, number>;
  /** Part names to remove from parent (optional) */
  removeParts?: string[];
  /** Builder names to remove from parent (optional) */
  removeBuilders?: string[];
}

/**
 * Expanded blueprint definition (runtime format)
 */
export interface BlueprintDef {
  name: string;
  parts: PartDef[];
  tags: Record<string, string>;
  stats: Record<string, number>;
}

/**
 * Part definition within a blueprint
 */
export interface PartDef {
  name: string;
  params: Record<string, unknown>;
}

/**
 * Standard events fired during object lifecycle
 */
export const Events = {
  /** Fired after all parts are attached */
  OBJECT_CREATED: 'objectCreated',
  /** Fired when object position changes */
  POSITION_CHANGED: 'positionChanged',
  /** Fired before object is destroyed */
  DESTROYING: 'destroying',
} as const;
